<template>
  <USelectMenu
    :model-value="modelValue"
    :items="selectItems"
    :placeholder="placeholder ?? $t('general.any')"
    :by="compareValues"
    value-key="value"
    label-key="label"
    :filter-fields="['label', 'searchText']"
    :search-input="options.length > 5 ? { placeholder: $t('general.filter') } : false"
    multiple
    class="w-full"
    :ui="{
      value: 'truncate',
      itemWrapper: 'min-w-0',
      itemLabel: 'truncate',
      content: 'min-w-72',
      viewport: 'max-h-72',
    }"
    @update:model-value="(value: any) => emitUpdate(Array.isArray(value) ? value : [])"
  >
    <template #item-trailing="{ item }">
      <span v-if="item.count !== undefined" class="shrink-0 text-xs text-gray-500 dark:text-gray-400">
        {{ formatCount(item.count) }}
      </span>
    </template>
  </USelectMenu>
</template>

<script setup lang="ts">
import type { OptionGroup } from '@/components/elements/facetedFilter/utils'

interface Props {
  modelValue?: any[]
  options?: any[]
  optionCount?: (option: any) => number | undefined
  placeholder?: string
}

const props = withDefaults(defineProps<Props>(), {
  modelValue: () => [],
  options: () => [],
  optionCount: undefined,
  placeholder: undefined,
})

const emit = defineEmits<{
  (e: 'update:modelValue', value: any[]): void
}>()

const optionLabel = (option: any): string => option?.label ?? option?.text ?? option?.title ?? ''

const optionValue = (option: any) => option?.value ?? option

const groupedOptions = computed<OptionGroup[]>(() => {
  const groups: OptionGroup[] = []
  let current: OptionGroup = { label: null, options: [] }

  for (const option of props.options ?? []) {
    if (option?.type === 'label') {
      if (current.options.length) groups.push(current)
      current = { label: option.title ?? option.label ?? '', options: [] }
    } else if (Array.isArray(option?.options)) {
      if (current.options.length) {
        groups.push(current)
        current = { label: null, options: [] }
      }
      groups.push({ label: option.label ?? option.title ?? null, options: option.options })
    } else {
      current.options.push(option)
    }
  }

  if (current.options.length) groups.push(current)
  return groups
})

const filteredGroups = computed(() => {
  return groupedOptions.value
})

const selectItems = computed(() => {
  return filteredGroups.value.map((group) => {
    const items = group.options.map((option) => ({
      ...option,
      value: optionValue(option),
      label: optionLabel(option),
      searchText: [optionLabel(option), option?.text, option?.title, option?.name].filter(Boolean).join(' '),
      count: getCount(option),
    }))

    return group.label
      ? [{ type: 'label' as const, label: group.label }, ...items]
      : items
  })
})

const valueKey = (value: any): string => {
  if (value == null) return ''
  if (typeof value === 'object') return String(value.id ?? value.idSegment ?? value.idForm ?? value.idProduct ?? value.idService ?? JSON.stringify(value))
  return String(value)
}

const compareValues = (a: any, b: any): boolean => a === b || valueKey(a) === valueKey(b)

const emitUpdate = (value: any[]) => {
  emit('update:modelValue', value)
}

const getCount = (option: any): number | undefined => props.optionCount?.(option)

const formatCount = (n: number): string => {
  if (n >= 1000) return `${(n / 1000).toFixed(1).replace(/\.0$/, '')}k`
  return String(n)
}
</script>
